import React from "react";
import { FormattedMessage } from "react-intl";
import {
	Card,
	CircularProgress,
	Grid,
	IconButton,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";

import { type PickZone } from "@/schemas";

import { PickZoneEditButton } from "../components/pick-zone-edit-button";
import { PickZoneDeleteButton } from "../components/pick-zone-delete-button";
import { PickZoneHistoryButton } from "../components/pick-zone-history-button";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(even)": {
		backgroundColor: theme.palette.action.hover,
	},
}));

export interface PickZonesTemplateProps {
	readonly pickZones: PickZone[];
	readonly isLoading: boolean;
	readonly isError: boolean;
	readonly onRetry: () => void;
}

export const PickZonesTemplate: React.FC<PickZonesTemplateProps> = ({ pickZones, isLoading, isError, onRetry }) => {
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(50);

	return (
		<Card component={Grid} container direction="column">
			<TableContainer sx={{ flex: 1 }}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>
								<FormattedMessage id="Name" defaultMessage="Name" />
							</TableCell>
							<TableCell>
								<FormattedMessage id="Description" defaultMessage="Description" />
							</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoading === true && (
							<StyledTableRow sx={{ height: 100 }}>
								<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
									<CircularProgress color="info" data-testid="pick-zones__progress__loading" />
								</TableCell>
							</StyledTableRow>
						)}
						{isError === true && isLoading === false && (
							<StyledTableRow sx={{ height: 100 }}>
								<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
									<Grid container justifyContent="center" alignItems="center" gap={2}>
										<Typography component="span" color="error" data-testid="pick-zones__text__error">
											<FormattedMessage
												id="Failed to load pick zones. Please try again."
												defaultMessage="Failed to load pick zones. Please try again."
											/>
										</Typography>
										<IconButton onClick={onRetry} data-testid="pick-zones__button__retry">
											<Refresh />
										</IconButton>
									</Grid>
								</TableCell>
							</StyledTableRow>
						)}
						{!isLoading && !isError && pickZones.length === 0 && (
							<StyledTableRow>
								<TableCell colSpan={1000} align="center">
									<Typography color="textDisabled" data-testid="pick-zones__text__no-pick-zones">
										<FormattedMessage id="No Pick Zones Configured" defaultMessage="No Pick Zones Configured" />
									</Typography>
								</TableCell>
							</StyledTableRow>
						)}
						{!isLoading &&
							!isError &&
							pickZones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((pickZone) => (
								<StyledTableRow key={pickZone.id}>
									<TableCell sx={{ fontWeight: 500 }}>{pickZone.alias}</TableCell>
									<TableCell>{pickZone.description}</TableCell>
									<TableCell>
										<Grid container gap={1} justifyContent="flex-end">
											<PickZoneHistoryButton pickZone={pickZone} />
											<PickZoneEditButton pickZone={pickZone} />
											<PickZoneDeleteButton pickZone={pickZone} />
										</Grid>
									</TableCell>
								</StyledTableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[50, 100, 500]}
				component="div"
				count={pickZones.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={(_, pageNumber) => setPage(pageNumber)}
				onRowsPerPageChange={(event) => {
					setRowsPerPage(parseInt(event.target.value));
					setPage(0);
				}}
			/>
		</Card>
	);
};
